'use client';

import type { SmokeTestHistoryResponseDto as TraceViewResponseDto } from '@terra/shared-contracts-smoke-test-history';
import { TodoItemSchema } from '@terra/shared-contracts-smoke-test-history';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type { SseSubscribe } from '@terra/shared-sse-contracts';
import {
  Box,
  css,
  Divider,
  HStack,
  token,
  VStack,
} from '@terra/ui-styled-system';
import { Loading03Icon, SearchAddIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { z } from 'zod';
import { Scroll } from '../../..';
import { Splitter } from '../../../components/splitter';
import { Text } from '../../../components/styled/text';
import { BodyDefault } from '../../../components/typography';
import { TraceBlockedEmptyState } from './components/TraceBlockedEmptyState';
import { TraceViewHeader } from './TraceViewHeader';
import { TraceMessageCard } from './TraceMessageCard';
import { useTraceViewSettings } from './TraceViewSettingsContext';
import { TraceViewToolbar, type TraceViewMode } from './TraceViewToolbar';
import type { PlanTodoItem, TraceMessage } from './types';
import { parseChatHistory } from './types';
import { useStoryReplay } from './hooks/useStoryReplay';
import { hasEndTestCall } from '@terra/shared-chat-trace/trace-view/utils/end-tool-content';
import { shouldHideInCondensedView } from './utils/tool-categories';
import { messageContainsOperationId } from './utils/highlight-operation';
import { normalizeToolName } from './utils/tool-config';
import {
  STORY_SUMMARY_STEP_KEY,
  resolveStoryStepMetaFromMessage,
} from './utils/story-step-metadata';
import { shouldIncludeStoryMessage } from './utils/story-source-filter';
import type { TracePhase } from './utils/trace-tool-filters';

const scrollableStyles = css({
  borderRadius: 'md',
  paddingInlineStart: 2,
  paddingInlineEnd: 2,
  paddingBlockStart: 2,
  paddingBlockEnd: 4,
});
const scrollableStylesFlush = css({
  borderRadius: 'md',
  paddingInlineStart: 2,
  paddingInlineEnd: 2,
  paddingBlockStart: 0,
  paddingBlockEnd: 4,
});

export type TraceViewHandle = {
  getScrollElement: () => HTMLDivElement | null;
};
export type TraceViewProps = {
  history: TraceViewResponseDto;
  maxHeight?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  rightPanel?: ReactNode;
  status?: 'running' | 'completed' | 'error' | 'aborted';
  showHeader?: boolean;
  showToolbar?: boolean;
  forcedViewMode?: TraceViewMode;
  tracePhase?: TracePhase;
  preferHarOverToolOutput?: boolean;
  toolbarExtra?: ReactNode;
  highlightOperationId?: string;
  onStepVisibilityChange?: (input: {
    stepKey: string;
    visible: boolean;
  }) => void;
  onSSESubscribe?: SseSubscribe;
};

function withRenderableContent(messages: TraceMessage[]): TraceMessage[] {
  return messages.filter((message) =>
    message.content.some((content) => {
      if (content.type === 'text') return content.text.trim().length > 0;
      if (content.type === 'tool_call') return true;
      if (content.type === 'reasoning') return content.text.trim().length > 0;

      return false;
    }),
  );
}

type TraceViewInnerProps = TraceViewProps & {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  scrollRef?: RefObject<HTMLDivElement | null>;
};

function TraceViewInner({
  maxHeight = '100%',
  isLoading = false,
  emptyMessage = 'No history available',
  history,
  rightPanel,
  searchQuery,
  setSearchQuery,
  scrollRef,
  status: statusProp,
  showHeader = true,
  showToolbar = true,
  forcedViewMode,
  tracePhase = 'attack',
  preferHarOverToolOutput,
  toolbarExtra,
  highlightOperationId,
  onStepVisibilityChange,
  onSSESubscribe,
}: TraceViewInnerProps) {
  const t = useTranslations('designSystem.signals.drawer.traceView.viewMode');
  const tExec = useTranslations('designSystem.executions');
  const {
    settings,
    setShowHttpViewGlobal,
    setShowCondensedView,
    setSourceFilter,
    setShowHiddenSteps,
  } = useTraceViewSettings();
  const showHttpViewGlobal = settings.showHttpViewGlobal;
  const showCondensedView =
    forcedViewMode === undefined
      ? settings.showCondensedView
      : forcedViewMode === 'minimized';
  const isStoryTrace = history.isStoryTrace === true;
  const storySteps = history.steps;
  const allMessages = useMemo(() => {
    if (!history.messages || history.messages.length === 0) {
      return [];
    }

    return parseChatHistory(history.messages as TerraUIMessage[], tracePhase);
  }, [history.messages, tracePhase]);
  const hasEndTestToolCall = useMemo(
    () => hasEndTestCall((history.messages ?? []) as TerraUIMessage[]),
    [history.messages],
  );
  // Extract plan from the latest todo-write tool call with fullPlan metadata
  const planFromMessages = useMemo((): PlanTodoItem[] | null => {
    const PlanArraySchema = z.array(TodoItemSchema);

    for (let i = allMessages.length - 1; i >= 0; i--) {
      const msg = allMessages[i];

      for (const content of msg.content) {
        if (
          content.type === 'tool_call' &&
          normalizeToolName(content.toolCall.name) === 'todo-write' &&
          content.toolCall.metadata?.fullPlan
        ) {
          const parseResult = PlanArraySchema.safeParse(
            content.toolCall.metadata.fullPlan,
          );

          if (parseResult.success) {
            return parseResult.data;
          }
        }
      }
    }

    return null;
  }, [allMessages]);
  const effectivePlan = history.plan ?? planFromMessages ?? undefined;
  const status = useMemo((): 'running' | 'completed' | 'error' | 'aborted' => {
    if (statusProp != null) return statusProp;
    if (history.status) {
      switch (history.status) {
        case 'RUNNING':
          return 'running';
        case 'COMPLETED':
          return 'completed';
        case 'FAILED':
          return 'error';
        case 'PAUSED':
          return 'running';
        case 'ABORTED':
          return 'aborted';
        case 'BLOCKED':
          return 'completed';
        default:
          break;
      }
    }

    return 'completed';
  }, [statusProp, history.status]);
  const visibleMessages = useMemo(
    () => withRenderableContent(allMessages),
    [allMessages],
  );
  const filteredMessages = useMemo(() => {
    let msgs = visibleMessages;

    if (showCondensedView) {
      const wouldBeCompletelyHidden = (message: TraceMessage): boolean => {
        const toolCalls = message.content.filter((c) => c.type === 'tool_call');

        if (toolCalls.length === 0) return false;
        const visibleToolCalls = toolCalls.filter((c) => {
          if (c.type !== 'tool_call') return false;
          if (c.toolCall.error) return true;

          return !shouldHideInCondensedView(c.toolCall.name);
        });

        return visibleToolCalls.length === 0;
      };

      const isTextOnlyMessage = (message: TraceMessage): boolean => {
        return message.content.every(
          (c) => c.type === 'text' || c.type === 'reasoning',
        );
      };

      msgs = msgs
        .map((message, index) => {
          const originalToolCalls = message.content.filter(
            (c) => c.type === 'tool_call',
          );
          const hasOriginalToolCalls = originalToolCalls.length > 0;
          const filteredContent = message.content.filter((content) => {
            if (content.type === 'tool_call') {
              if (content.toolCall.error) return true;

              return !shouldHideInCondensedView(content.toolCall.name);
            }

            return true;
          });
          const hasRemainingToolCalls = filteredContent.some(
            (c) => c.type === 'tool_call',
          );

          if (hasOriginalToolCalls && !hasRemainingToolCalls) {
            return { ...message, content: [] };
          }

          if (isTextOnlyMessage(message) && filteredContent.length > 0) {
            let nextIdx = index + 1;

            while (nextIdx < visibleMessages.length) {
              const nextMessage = visibleMessages[nextIdx];
              const nextHasToolCalls = nextMessage.content.some(
                (c) => c.type === 'tool_call',
              );

              if (nextHasToolCalls) {
                if (wouldBeCompletelyHidden(nextMessage)) {
                  return { ...message, content: [] };
                }

                break;
              }

              nextIdx++;
            }
          }

          return { ...message, content: filteredContent };
        })
        .filter((message) => message.content.length > 0);
    }

    if (isStoryTrace) {
      msgs = msgs.filter((message) => {
        const storyMeta = resolveStoryStepMetaFromMessage({
          message,
          steps: storySteps,
        });

        return shouldIncludeStoryMessage({
          storyMeta,
          sourceFilter: settings.sourceFilter,
          showHiddenSteps: settings.showHiddenSteps,
        });
      });
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();

      msgs = msgs.filter((msg) => {
        const contentMatch = msg.content.some((c) => {
          if (c.type === 'text') {
            return c.text.toLowerCase().includes(query);
          }

          if (c.type === 'tool_call') {
            const nameMatch = c.toolCall.name.toLowerCase().includes(query);
            const argsMatch = JSON.stringify(c.toolCall.args || {})
              .toLowerCase()
              .includes(query);
            const outputMatch = c.toolCall.output
              ?.toLowerCase()
              .includes(query);
            const errorMatch = c.toolCall.error?.toLowerCase().includes(query);

            return nameMatch || argsMatch || outputMatch || errorMatch;
          }

          return false;
        });

        return contentMatch;
      });
    }

    return msgs;
  }, [
    visibleMessages,
    searchQuery,
    showCondensedView,
    isStoryTrace,
    storySteps,
    settings.sourceFilter,
    settings.showHiddenSteps,
  ]);
  const postAttackMessages = useMemo(() => {
    if (
      !history.postAttackMessages ||
      history.postAttackMessages.length === 0
    ) {
      return [];
    }

    const parsed = parseChatHistory(
      history.postAttackMessages as TerraUIMessage[],
      'post-attack',
    );

    return withRenderableContent(parsed);
  }, [history.postAttackMessages]);
  const highlightedScrollAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    highlightedScrollAttemptedRef.current = null;
  }, [highlightOperationId]);
  useEffect(() => {
    if (!highlightOperationId || isLoading) {
      return;
    }

    if (highlightedScrollAttemptedRef.current === highlightOperationId) {
      return;
    }

    const hasTargetMessage = [...filteredMessages, ...postAttackMessages].some(
      (message) =>
        messageContainsOperationId({
          message,
          operationId: highlightOperationId,
        }),
    );

    if (!hasTargetMessage) {
      return;
    }

    highlightedScrollAttemptedRef.current = highlightOperationId;
    requestAnimationFrame(() => {
      const scrollElement = scrollRef?.current;

      if (!scrollElement) {
        return;
      }

      const target = scrollElement.querySelector(
        `[data-highlight-operation-id="${CSS.escape(highlightOperationId)}"]`,
      );

      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [
    filteredMessages,
    postAttackMessages,
    highlightOperationId,
    isLoading,
    scrollRef,
  ]);
  const storyReplay = useStoryReplay({ items: filteredMessages });

  if (isLoading) {
    return (
      <Box
        h={maxHeight}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" color="fg.muted">
          Loading history...
        </Text>
      </Box>
    );
  }

  const hasNoDataAtAll = visibleMessages.length === 0;
  const hasNoFilteredResults =
    filteredMessages.length === 0 && visibleMessages.length > 0;
  const attackFailureReason =
    history.metadata?.skipAttackReason &&
    (status === 'error' || status === 'aborted')
      ? history.metadata.skipAttackReason
      : null;
  const firstUserMessageIndex = filteredMessages.findIndex(
    (msg) => msg.role === 'user',
  );
  const showRunningIndicator =
    status === 'running' && !searchQuery && !hasEndTestToolCall;
  const executionLogToolbar =
    showToolbar && visibleMessages.length > 0 ? (
      <Box w="fit-content">
        <TraceViewToolbar
          viewMode={showCondensedView ? 'minimized' : 'full'}
          onViewModeChange={(mode: TraceViewMode) => {
            if (forcedViewMode !== undefined) return;
            setShowCondensedView(mode === 'minimized');
          }}
          showHttpOnly={showHttpViewGlobal}
          onShowHttpOnlyChange={setShowHttpViewGlobal}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          showSearch={visibleMessages.length > 0}
          toolbarExtra={toolbarExtra}
          showStoryControls={isStoryTrace}
          sourceFilter={settings.sourceFilter}
          onSourceFilterChange={setSourceFilter}
          showHiddenSteps={settings.showHiddenSteps}
          onShowHiddenStepsChange={setShowHiddenSteps}
          isReplaying={storyReplay.isReplaying}
          onReplayToggle={storyReplay.toggle}
        />
      </Box>
    ) : null;
  const traceContent = (
    <VStack h="full" w="full" minH={0} gap={0} alignItems="stretch">
      {!showHeader && executionLogToolbar ? (
        <Box flexShrink={0} pb={2} display="flex" justifyContent="flex-end">
          {executionLogToolbar}
        </Box>
      ) : null}
      {/* Fixed Header with Plan, Status, Summary */}
      {showHeader && (
        <Box flexShrink={0} pb={2}>
          <TraceViewHeader
            plan={effectivePlan}
            status={status}
            skipAttackReasonType={history.metadata?.skipAttackReasonType}
            disableDetailedView={Boolean(history.metadata?.skipAttackReason)}
            executionLogToolbar={executionLogToolbar}
          />
        </Box>
      )}

      {hasNoDataAtAll ? (
        attackFailureReason ? (
          <TraceBlockedEmptyState
            reason={attackFailureReason}
            skipAttackReasonType={history.metadata?.skipAttackReasonType}
          />
        ) : (
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <VStack gap={2} alignItems="center">
              {status === 'running' && (
                <Box animation="spin 1s linear infinite" display="flex">
                  <Loading03Icon size={20} color={token('colors.fg.muted')} />
                </Box>
              )}
              <BodyDefault color="fg.muted">
                {status === 'running' ? t('executionInProgress') : emptyMessage}
              </BodyDefault>
            </VStack>
          </Box>
        )
      ) : hasNoFilteredResults ? (
        <>
          {showHeader ? <Divider /> : null}
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="sm" color="fg.muted">
              {searchQuery.trim()
                ? t('noResultsFound', { query: searchQuery })
                : emptyMessage}
            </Text>
          </Box>
        </>
      ) : (
        visibleMessages.length > 0 && (
          <>
            {showHeader ? <Divider /> : null}

            {/* Scrollable Tool Calls */}
            <>
              {searchQuery && (
                <Text fontSize="2xs" color="fg.muted" pb={1}>
                  {`${filteredMessages.length} of ${visibleMessages.length} results`}
                </Text>
              )}
              <Box flex={1} minH={0} overflow="hidden" pt={showHeader ? 2 : 0}>
                <Scroll
                  ref={scrollRef}
                  className={
                    showHeader ? scrollableStyles : scrollableStylesFlush
                  }
                  orientation="vertical"
                  autoScroll={status === 'running' || storyReplay.isReplaying}
                  showScrollbar="hover"
                  gap={1}
                >
                  <VStack
                    gap={3}
                    alignItems="stretch"
                    w="full"
                    paddingInline={2}
                  >
                    {storyReplay
                      .revealed(filteredMessages)
                      .map((message, index) => {
                        const storyMeta = isStoryTrace
                          ? resolveStoryStepMetaFromMessage({
                              message,
                              steps: storySteps,
                            })
                          : null;

                        return (
                          <TraceMessageCard
                            key={message.id || `msg-${index}`}
                            message={message}
                            isFirstUserMessage={
                              message.role === 'user' &&
                              index === firstUserMessageIndex
                            }
                            tracePhase={tracePhase}
                            preferHarOverToolOutput={preferHarOverToolOutput}
                            highlightOperationId={highlightOperationId}
                            storyStepVisible={storyMeta?.visible ?? true}
                            onToggleStepVisibility={
                              storyMeta &&
                              storyMeta.stepKey !== STORY_SUMMARY_STEP_KEY &&
                              onStepVisibilityChange
                                ? () =>
                                    onStepVisibilityChange({
                                      stepKey: storyMeta.stepKey,
                                      visible: !storyMeta.visible,
                                    })
                                : undefined
                            }
                            onSSESubscribe={onSSESubscribe}
                          />
                        );
                      })}
                    {/* Show thinking indicator when trace is still running */}
                    {showRunningIndicator && (
                      <Box
                        py={3}
                        px={2}
                        color="fg.muted"
                        display="flex"
                        alignItems="center"
                      >
                        <Box
                          animationStyle="three-dots-10px"
                          aria-label="3 dots animation"
                          width="4px"
                          aspectRatio="1"
                          borderRadius="50%"
                          display="inline-block"
                          opacity={0.55}
                          transform="scale(0.9)"
                          _dark={{
                            // temp fix for dark mode
                            filter: 'invert(100%)',
                            opacity: 0.45,
                          }}
                        />
                      </Box>
                    )}

                    {postAttackMessages.length > 0 && (
                      <>
                        <HStack gap={2} alignItems="center" py={3} px={2}>
                          <Divider flex={1} />
                          <HStack gap={1.5} alignItems="center" flexShrink={0}>
                            <SearchAddIcon
                              size={12}
                              color={token('colors.fg.muted')}
                            />
                            <Text
                              fontSize="xs"
                              fontWeight="medium"
                              color="fg.muted"
                              whiteSpace="nowrap"
                            >
                              {tExec('validation')}
                            </Text>
                          </HStack>
                          <Divider flex={1} />
                        </HStack>

                        {postAttackMessages.map((message, index) => (
                          <TraceMessageCard
                            key={`post-attack-${message.id || index}`}
                            message={message}
                            isFirstUserMessage={false}
                            tracePhase="post-attack"
                            highlightOperationId={highlightOperationId}
                          />
                        ))}
                      </>
                    )}
                  </VStack>
                </Scroll>
              </Box>
            </>
          </>
        )
      )}
    </VStack>
  );

  // If rightPanel is provided, use resizable split layout
  if (rightPanel) {
    return (
      <Splitter.Root
        orientation="horizontal"
        h={maxHeight}
        w="full"
        panels={[{ id: 'trace' }, { id: 'chat' }]}
        defaultSize={[60, 40]}
        borderWidth="0"
        gap={0}
      >
        <Splitter.Panel id="trace" borderWidth="0" w="full">
          <Box
            h="full"
            w="full"
            overflow="hidden"
            borderRightWidth="1px"
            borderColor="border.subtle"
          >
            <Box p={4} h="full" w="full">
              {traceContent}
            </Box>
          </Box>
        </Splitter.Panel>

        <Splitter.ResizeTrigger
          id="trace:chat"
          width="0px"
          minWidth="0px"
          bg="transparent"
          position="relative"
          _after={{
            content: '""',
            position: 'absolute',
            inset: '0 4px 0 -4px',
            width: '10px',
            bg: 'transparent',
            cursor: 'col-resize',
          }}
        />

        <Splitter.Panel id="chat" borderWidth="0" borderRadius={0} w="full">
          <Box h="full" w="full" overflow="hidden">
            {rightPanel}
          </Box>
        </Splitter.Panel>
      </Splitter.Root>
    );
  }

  return (
    <Box
      h={maxHeight}
      w="full"
      minH={0}
      overflow="hidden"
      display="flex"
      flexDirection="column"
      p={showHeader ? 4 : 0}
    >
      {traceContent}
    </Box>
  );
}

/**
 * TraceView - uses TraceViewSettingsContext if provided, otherwise works standalone
 */
export const TraceView = forwardRef<TraceViewHandle, TraceViewProps>(
  function TraceView(props, ref) {
    const [searchQuery, setSearchQuery] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      getScrollElement: () => scrollRef.current,
    }));

    return (
      <TraceViewInner
        {...props}
        scrollRef={scrollRef}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    );
  },
);
